/**
 * Dragging notes onto a folder.
 *
 * The payload is note ids under a private MIME type — never note text. A drag
 * can end up over another application, and the ids mean nothing outside this
 * one, whereas the body of a note might mean a great deal.
 */
export const NOTE_IDS_MIME = 'application/x-indigonotes-note-ids'

export function setDragIds(dt: DataTransfer, ids: string[]): void {
  dt.setData(NOTE_IDS_MIME, JSON.stringify(ids))
  dt.effectAllowed = 'move'
}

export function readDragIds(dt: DataTransfer | null): string[] {
  if (!dt) return []
  try {
    const raw = dt.getData(NOTE_IDS_MIME)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

/**
 * Whether a drag in flight is one of ours. The payload itself cannot be read
 * until the drop — the browser withholds it during dragover — but the list of
 * types is available, which is enough to decide whether to light a target up.
 */
export function isNoteDrag(dt: DataTransfer | null): boolean {
  return !!dt && Array.from(dt.types).includes(NOTE_IDS_MIME)
}

/**
 * What to drag when the grab starts on `id`: the whole selection if that note
 * is part of it, otherwise just that note. Dragging a note outside the
 * selection should not silently carry the selection along with it.
 */
export function dragPayload(id: string, selection: string[]): string[] {
  return selection.length > 1 && selection.includes(id) ? selection : [id]
}
